/**
 * The complete permission vocabulary.
 *
 * Permissions are granted to roles, and checks are always written against a
 * permission — never against a role or a principal type. Checking `role === "admin"`
 * at a call site scatters policy across the codebase and makes it impossible to
 * answer "what can a supervisor do?" without reading every route.
 *
 * Naming: `<domain>:<resource>:<action>`.
 */
export const PERMISSIONS = {
    /** General product/company questions. The only capability a guest holds. */
    CHAT_GENERAL_USE: "chat:general:use",
    /** Conversations that may touch the customer's own account data. */
    CHAT_ACCOUNT_USE: "chat:account:use",
    /** A customer reading their own records. Never another customer's. */
    ACCOUNT_PROFILE_READ: "account:profile:read",
    ACCOUNT_ORDERS_READ: "account:orders:read",
    ACCOUNT_REWARDS_READ: "account:rewards:read",
    ACCOUNT_TRACKING_READ: "account:tracking:read",
    /** Support staff acting on customer conversations. */
    SUPPORT_CONVERSATIONS_READ: "support:conversations:read",
    SUPPORT_CONVERSATIONS_WRITE: "support:conversations:write",
    SUPPORT_CONVERSATIONS_ASSIGN: "support:conversations:assign",
    /** Erasing a conversation outright. Administrators only — see below. */
    SUPPORT_CONVERSATIONS_DELETE: "support:conversations:delete",
    /** Reading a customer's account data in the course of supporting them. */
    SUPPORT_CUSTOMER_READ: "support:customer:read",
    /** Administrative control of internal accounts. */
    ADMIN_AGENTS_READ: "admin:agents:read",
    ADMIN_AGENTS_WRITE: "admin:agents:write",
    ADMIN_AUDIT_READ: "admin:audit:read",
    /**
     * Configuring how the assistant speaks.
     *
     * Split from the knowledge permissions because they are different kinds of risk.
     * Changing the persona alters tone; approving a document decides what the
     * assistant may assert on the company's behalf, which for a regulated product is
     * the more consequential act.
     */
    ADMIN_ASSISTANT_READ: "admin:assistant:read",
    ADMIN_ASSISTANT_WRITE: "admin:assistant:write",
    /** The knowledge corpus: what the assistant is allowed to answer from. */
    ADMIN_KNOWLEDGE_READ: "admin:knowledge:read",
    ADMIN_KNOWLEDGE_WRITE: "admin:knowledge:write",
    /**
     * The catalogue projection: what customers are shown for sale.
     *
     * A third permission rather than a reuse of the knowledge one, for the same reason
     * the assistant's voice is already separate from its knowledge. Approving a
     * document decides what the assistant may assert; refreshing the catalogue decides
     * which products appear, at what price, as available. Merchandising is its own kind
     * of consequence.
     *
     * Write only. Reading the catalogue needs no permission beyond a session, because
     * it is the shop's public shelf.
     */
    ADMIN_CATALOG_WRITE: "admin:catalog:write",
    /**
     * Aggregate reporting.
     *
     * Granted to supervisors as well as administrators: running a support team means
     * seeing how it performs, and the figures are aggregates that name no customer.
     */
    ADMIN_ANALYTICS_READ: "admin:analytics:read",
};
/**
 * Capabilities of an anonymous visitor.
 *
 * Deliberately a single entry. Every addition here is a decision to expose
 * something to an unauthenticated caller, and should be reviewed as such.
 */
const GUEST_PERMISSIONS = [PERMISSIONS.CHAT_GENERAL_USE];
/** Capabilities of a verified customer: their own data, and nothing else. */
const CUSTOMER_PERMISSIONS = [
    PERMISSIONS.CHAT_GENERAL_USE,
    PERMISSIONS.CHAT_ACCOUNT_USE,
    PERMISSIONS.ACCOUNT_PROFILE_READ,
    PERMISSIONS.ACCOUNT_ORDERS_READ,
    PERMISSIONS.ACCOUNT_REWARDS_READ,
    PERMISSIONS.ACCOUNT_TRACKING_READ,
];
/**
 * Staff capabilities, defined cumulatively.
 *
 * Each role is a strict superset of the one below it, which is what makes
 * "a supervisor can do anything an agent can" true by construction rather than by
 * remembering to copy entries.
 */
const AGENT_BASE_PERMISSIONS = [
    PERMISSIONS.SUPPORT_CONVERSATIONS_READ,
    PERMISSIONS.SUPPORT_CONVERSATIONS_WRITE,
    PERMISSIONS.SUPPORT_CUSTOMER_READ,
];
const SUPERVISOR_PERMISSIONS = [
    ...AGENT_BASE_PERMISSIONS,
    PERMISSIONS.SUPPORT_CONVERSATIONS_ASSIGN,
    // Aggregates only, naming no customer. A supervisor who cannot see the escalation
    // rate cannot do the job the role exists for.
    PERMISSIONS.ADMIN_ANALYTICS_READ,
];
const ADMIN_PERMISSIONS = [
    ...SUPERVISOR_PERMISSIONS,
    /**
     * Deliberately not granted to agents or supervisors.
     *
     * Every other support capability is recoverable: a wrongly closed
     * conversation reopens, a wrong assignment reassigns. This one is not — the
     * transcript, the notes and the customer's attachments are gone — so it sits
     * with the person accountable for the shop rather than with everyone
     * answering its queue.
     */
    PERMISSIONS.SUPPORT_CONVERSATIONS_DELETE,
    PERMISSIONS.ADMIN_AGENTS_READ,
    PERMISSIONS.ADMIN_AGENTS_WRITE,
    PERMISSIONS.ADMIN_AUDIT_READ,
    PERMISSIONS.ADMIN_ASSISTANT_READ,
    PERMISSIONS.ADMIN_ASSISTANT_WRITE,
    PERMISSIONS.ADMIN_KNOWLEDGE_READ,
    PERMISSIONS.ADMIN_KNOWLEDGE_WRITE,
    PERMISSIONS.ADMIN_CATALOG_WRITE,
];
const ROLE_PERMISSIONS = {
    agent: AGENT_BASE_PERMISSIONS,
    supervisor: SUPERVISOR_PERMISSIONS,
    admin: ADMIN_PERMISSIONS,
};
/** Every permission held by a principal. */
export function permissionsFor(principal) {
    switch (principal.type) {
        case "guest":
            return GUEST_PERMISSIONS;
        case "customer":
            return CUSTOMER_PERMISSIONS;
        case "agent":
            return ROLE_PERMISSIONS[principal.role];
    }
}
/** Every permission held by a staff role. Used when provisioning accounts. */
export function permissionsForRole(role) {
    return ROLE_PERMISSIONS[role];
}
export function hasPermission(principal, permission) {
    return permissionsFor(principal).includes(permission);
}
/** True only if the principal holds every permission listed. */
export function hasAllPermissions(principal, permissions) {
    const held = permissionsFor(principal);
    return permissions.every((permission) => held.includes(permission));
}
//# sourceMappingURL=permissions.js.map